import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { GIVING_CAMPAIGNS, FOSTER_CHAMPIONS, CHILDREN_UNITED_HOPE } from "../data/kids";
import SEO from "../components/SEO";

const PARTNERS = [
  {
    name: "HALFAMILLIONKIDS.ORG",
    tagline: "Dedicated to Foster & Trafficked Children",
    logo: "/assets/partner-hmk-logo.jpg",
    url: "https://halfamillionkids.org/",
  },
  {
    name: "Being Beautiful Foundation",
    tagline: "Provides safe, secure and loving homes for children who have suffered abuse",
    logo: "/assets/partner-bbf-logo.png",
    logoBg: "#0a0a0a", // BBF logo is light-on-dark by design
    url: "https://beingbeautifulfoundation.org/",
  },
];

const OTHER_WAYS = [
  { i: "🌟", t: "Volunteer", d: "Help with events, mentorship, or care package assembly." },
  { i: "💌", t: "Fundraise", d: "Start a peer-to-peer fundraiser in your town or online." },
  { i: "🏢", t: "Corporate Giving", d: "Matching gifts, sponsorships, cause marketing opportunities." },
  { i: "📢", t: "Spread the Word", d: "Follow, share, and advocate. Awareness saves lives." },
];

export default function GetInvolved() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div data-testid="get-involved-page">
      <SEO
        path="/get-involved"
        title="Get Involved in Foster Care Advocacy | Volunteer, Donate & Advocate"
        description="Four ways to make a real difference for children in foster care — foster or adopt, volunteer and mentor, donate to foster care organizations, or become an advocate in your community."
      />
      <section className="relative overflow-hidden border-b border-fc-border bg-fc-ink">
        {/* Cinematic background — FULL color, no white-wash */}
        <div
          className="absolute inset-0 ken-burns"
          style={{ backgroundImage: `url(${FOSTER_CHAMPIONS})`, backgroundSize: "cover", backgroundPosition: "center" }}
          aria-hidden="true"
        />
        {/* Dark scrim — left-heavy so hero copy reads white */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/25" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-b from-transparent to-black/70" aria-hidden="true" />

        <div className="container-xl relative pt-20 pb-20 md:pt-24 md:pb-24">
          <div className="overline text-fc-accent mb-5">Ways to Give · Every Day Counts</div>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.95] max-w-5xl text-white">
            Turn 365 days of waiting into <span className="italic text-fc-accent">365 days of hope.</span>
          </h1>
          <p className="mt-6 text-white/85 text-lg max-w-3xl leading-relaxed">
            At Foster Care Awareness 365, every action — every gift — moves us closer to a future where no child feels forgotten. Choose the way that moves your heart.
          </p>
        </div>
      </section>

      {/* Partner orgs (we tithe forward) */}
      <section className="bg-fc-surface border-b border-fc-border">
        <div className="container-xl py-16">
          <div className="overline text-fc-muted mb-4">Tithing Forward · 501(c)(3) Partners</div>
          <h2 className="font-display text-3xl md:text-4xl mb-8">We stand with these organizations.</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {PARTNERS.map((p) => (
              <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer" className="group border border-fc-border bg-fc-bg p-8 card-hover block" data-testid={`partner-${p.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <div
                  className="h-32 flex items-center justify-center mb-6 border border-fc-border overflow-hidden"
                  style={p.logoBg ? { backgroundColor: p.logoBg } : { backgroundColor: "#ffffff" }}
                >
                  <img
                    src={p.logo}
                    alt={`${p.name} logo`}
                    className="max-h-24 max-w-[80%] object-contain"
                    loading="lazy"
                  />
                </div>
                <div className="font-display text-xl mb-2">{p.name}</div>
                <div className="text-sm text-fc-muted italic">{p.tagline}</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Giving campaigns */}
      <section className="container-xl py-20">
        <div className="overline text-fc-muted mb-4">🔥 Current Giving Campaigns</div>
        <h2 className="font-display text-3xl md:text-5xl mb-12 leading-tight max-w-3xl">Because kids <em className="text-fc-accent">can't wait</em> another year.</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {GIVING_CAMPAIGNS.map((c, i) => (
            <div key={c.title} className="border border-fc-border bg-fc-bg p-7 card-hover" data-testid={`campaign-${i}`}>
              <div className="text-3xl mb-4">{c.emoji}</div>
              <div className="font-mono text-sm text-fc-accent mb-2">{c.goal}</div>
              <h3 className="font-display text-xl mb-3 leading-tight">{c.title}</h3>
              <p className="text-sm text-fc-muted leading-relaxed mb-5">{c.desc}</p>
              <a
                href="https://halfamillionkids.networkforgood.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="overline link-ul text-fc-ink inline-flex items-center gap-1"
                data-testid={`campaign-cta-${i}`}
              >
                Give → <ExternalLink size={11} className="opacity-60" />
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* Other ways */}
      <section className="bg-fc-ink text-fc-bg">
        <div className="container-xl py-20">
          <div className="overline opacity-70 mb-4">✨ Other Ways to Get Involved</div>
          <h2 className="font-display text-3xl md:text-5xl mb-10 max-w-3xl leading-tight">Giving isn't only about money.</h2>
          <div className="grid md:grid-cols-4 gap-0 border-t border-l border-fc-bg/20">
            {OTHER_WAYS.map((w) => (
              <div key={w.t} className="p-8 border-r border-b border-fc-bg/20">
                <div className="text-2xl mb-4">{w.i}</div>
                <div className="font-display text-xl mb-3">{w.t}</div>
                <p className="text-sm opacity-75 leading-relaxed">{w.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/contact" className="btn-primary" style={{background:"#F9F9F6", color:"#121212", borderColor:"#F9F9F6"}} data-testid="get-involved-contact">Start a Conversation</Link>
          </div>
        </div>
      </section>

      {/* Tithe + scripture */}
      <section className="container-xl py-20 max-w-4xl">
        <div className="overline text-fc-muted mb-4">Tithing = Giving</div>
        <h2 className="font-display text-3xl md:text-5xl mb-8 leading-tight">
          In the Bible, the <em className="text-fc-accent">Fatherless</em> are mentioned 43 times.
        </h2>
        <p className="text-fc-muted leading-relaxed mb-6">
          FosterCareAwareness365 tithes forward to <a href="https://halfamillionkids.org/" target="_blank" rel="noopener noreferrer" className="link-ul text-fc-ink">Half a Million Kids</a>, a 501(c)(3) charity actively making a difference for the 500,000+ kids in the U.S. foster care system. Many remain in the system, never find families, or age out — 50% become homeless, incarcerated, or government-dependent. Our hope is to make a small difference for the foster kids and adults dealing with circumstances life has dealt them.
        </p>
        <blockquote className="border-l-4 border-fc-accent pl-6 py-2 my-8 font-display text-xl md:text-2xl italic leading-tight">
          "Learn to do good; seek justice, reprove the ruthless, defend the orphans, plead for the widow."
          <div className="mt-3 text-base not-italic text-fc-muted font-sans">— Isaiah 1:17</div>
        </blockquote>
        <blockquote className="border-l-4 border-fc-accent pl-6 py-2 my-8 font-display text-xl md:text-2xl italic leading-tight">
          "Pure religion and undefiled before God and the Father is this: to visit the fatherless and widows in their affliction, and to keep himself unspotted from the world."
          <div className="mt-3 text-base not-italic text-fc-muted font-sans">— James 1:27</div>
        </blockquote>
        <div className="mt-10 text-fc-muted italic">— Tim Kelly</div>
      </section>

      {/* Final CTA */}
      <section className="bg-fc-surface border-t border-fc-border relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.2]">
          <img src={CHILDREN_UNITED_HOPE} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="container-xl py-16 text-center max-w-3xl relative">
          <h2 className="font-display text-3xl md:text-5xl mb-6 leading-tight">
            ❤️ Thank you for believing in <em className="text-fc-accent">their futures.</em>
          </h2>
          <p className="text-fc-muted leading-relaxed">
            Every gift — no matter the size — changes the story for a child who deserves to be seen, supported, and loved. Together, we are building a future where every day counts. <strong className="text-fc-ink">Because hope can't wait.</strong>
          </p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link to="/contact" className="btn-primary" data-testid="get-involved-cta-contact">Get in Touch</Link>
            <Link to="/meet-the-kids" className="btn-outline" data-testid="get-involved-cta-kids">Meet the Kids</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
